import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Grid,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  Paper,
  Modal,
  IconButton,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import {
  Breadcrumb,
  CTSinput,
  StyledTableCell,
  StyledTableRow,
} from '../../reusableComponent/Reusables';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import { Print } from '@mui/icons-material';
import styles from '../../../styles.module.css';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import ReusableSnackbar from '../../reusableComponent/ReusableSnackbar';
import qualitycontrolService from '../../../services/qualitycontrol.Service';
import cylinderMasterService from '../../../services/masters/cylinderMaster.Service';

// Print CSS
const printStyles = `
  @media print {
    body * {
      visibility: hidden;
    }
    .printable-area, .printable-area * {
      visibility: visible;
    }
    .printable-area {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
    }
    .no-print {
      display: none !important;
    }
    .printable-table {
      width: 100%;
      border-collapse: collapse;
    }
    .printable-table th, .printable-table td {
      padding: 8px;
      font-size: 12px;
    }
    .printable-image {
      max-width: 80px;
      max-height: 80px;
      object-fit: cover;
      vertical-align: middle;
    }
  }
`;

const QuickSearch = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location.state || null;
  dayjs.extend(customParseFormat);
  // Session Info
  const login_details = sessionStorage.getItem('loginUser');
  const userD = JSON.parse(login_details);
  const userInfo = userD.result.userInfo;

  // State
  const [successMessage, setSuccessMessage] = useState('');
  const [displayData, setDisplayData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [searchCylNo, setSearchCylNo] = useState('');
  const [searchTagNo, setSearchTagNo] = useState('');
  const [searchKey, setSearchKey] = useState('');

  // Form Setup
  const {
    control,
    watch,
    formState: { errors },
    setError,
  } = useForm({
    defaultValues: {
      entry_date_from: editData?.entry_date ? dayjs(editData.entry_date).format('YYYY-MM-DD') : '',
      entry_date_to: '',
      customer: editData?.customer || '',
    },
  });

  const entryDateFrom = watch('entry_date_from');
  const entryDateTo = watch('entry_date_to');

  // Inject print styles
  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.type = 'text/css';
    styleSheet.innerText = printStyles;
    document.head.appendChild(styleSheet);
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Data Fetch Function (modified to use provided data)
  const handleShow = useCallback(
    async (formData) => {


      try {
        setLoading(true);
        setDisplayData([]);
        setFilteredData([]);
        const response = await cylinderMasterService.getCylinderquicksearch();
        const fetchedData = response?.data || response || [];

        let srNo = 0;
        // Flatten and parse nested details safely
        const flattenedData = fetchedData.flatMap((item) => {
          let detailsArray = [];
          try {
            detailsArray = Array.isArray(item.table_details)
              ? item.table_details
              : JSON.parse(item.table_details || '[]');
          } catch (error) {
            console.error('Error parsing table_details:', error);
            detailsArray = [];
          }

          return detailsArray.map((row) => ({
            sr_no: ++srNo,
            date: item.current_location_date_time
              ? dayjs(item.current_location_date_time, 'DD/MM/YYYY hh:mm:ss A').format('YYYY-MM-DD')
              : 'N/A',
            // date: item.current_location_date_time,
            cyl_no: row.cyl_no || row.cylinder_no || 'N/A',
            tag_no: row.tag_no || 'N/A',
            current_location: row.current_location || item.current_location || 'N/A',
            status: row.status || item.status || 'N/A',
            note: row.note || row.note || 'N/A',
            trans_name: row.trans_name || 'N/A',
            // trans_date: row.trans_date || item.trans_date || 'N/A',
            trans_date: item.trans_date
              ? dayjs(item.trans_date, 'DD/MM/YYYY hh:mm:ss A').format('YYYY-MM-DD')
              : 'N/A',
          }));
        });

        setDisplayData(flattenedData);
        setFilteredData(flattenedData);
        setSuccessMessage(flattenedData.length === 0 ? 'No data found for the selected filters' : 'Data fetched successfully');
      } catch (error) {
        console.error('Error fetching data:', error);
        setSuccessMessage(error.message || 'Error fetching data');
        setDisplayData([]);
        setFilteredData([]);
      } finally {
        setLoading(false);
      }
    },
    [setError]
  );


  // Quick Search Function
  const handleQuickSearch = useCallback(async () => {
    setLoading(true);
    setSuccessMessage('');

    const key = searchKey.trim();

    if (!key) {
      setSuccessMessage('Please enter Cylinder No or Tag No');
      setLoading(false);
      return;
    }

    try {
      const response = await cylinderMasterService.getCylinderquicksearch({
        searchKey: key,
        page: 1,
        size: 100,
      });

      const fetchedData = response || [];
      let srNo = 0;

      const mappedData = fetchedData.map((item) => ({
        sr_no: ++srNo,
        date: item.current_location_date_time
          ? dayjs(item.current_location_date_time, 'DD/MM/YYYY hh:mm:ss A').format('YYYY-MM-DD')
          : 'N/A',
          // date: item.current_location_date_time,

        cyl_no: item.cylinder_no || 'N/A',
        tag_no: item.tag_no || 'N/A',
        current_location: item.current_location || 'N/A',
        status: item.status || 'N/A',
        remarks: item.note || 'N/A',
        trans_name: item.trans_name || 'N/A',
        // trans_date: item.trans_date || 'N/A',
          trans_date: item.trans_date
              ? dayjs(item.trans_date, 'DD/MM/YYYY hh:mm:ss A').format('YYYY-MM-DD')
              : 'N/A',
      }));

      setDisplayData(mappedData);
      setFilteredData(mappedData);

      setSuccessMessage(
        mappedData.length === 0
          ? 'No data found for the search criteria'
          : `Found ${mappedData.length} Successfully result(s)`
      );
    } catch (error) {
      console.error('Search Error:', error);
      setSuccessMessage(error.message || 'Error fetching data');
      setDisplayData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  }, [searchKey]);







  // Auto-fetch on date change
  useEffect(() => {
    if (entryDateFrom && entryDateTo && !errors.entry_date_from && !errors.entry_date_to) {
      handleShow({
        entry_date_from: entryDateFrom,
        entry_date_to: entryDateTo,
        customer: '',
      });
    }
  }, [entryDateFrom, entryDateTo, errors, handleShow]);

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedImage('');
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <Box className={styles.cts_page} sx={{ padding: '0px !important', margin: '0px !important' }}>
      <Box className={styles.body}>
        <Breadcrumb routeSegments={[{ name: 'Quick search' }]} />
        <Typography className={styles.header_txt}>QUICK SEARCH</Typography>

        {/* Form */}
        <form className="no-print">
          <Grid container spacing={3} alignItems="center" sx={{ mt: 2, px: 2 }}>

  {/* Cylinder No / Tag No Input */}
 <Grid container spacing={2} alignItems="center" sx={{ mt: 2, px: 2 }}>

  {/* Cylinder No / Tag No Input */}
  <Grid item xs={8} sm={9} md={10}>
    <TextField
      size="small"
      fullWidth
      value={searchKey}
      onChange={(e) => setSearchKey(e.target.value)}
      placeholder="Enter Cylinder No or Tag No"
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <SearchIcon color="action" />
          </InputAdornment>
        ),
      }}
      sx={{
        backgroundColor: '#f9f9f9',
        borderRadius: 1,
        '& .MuiOutlinedInput-root': {
          '&:hover fieldset': {
            borderColor: 'primary.main',
          },
        },
      }}
    />
  </Grid>

  {/* Search Button */}
  <Grid item xs={4} sm={3} md={2}>
    <Button
      variant="contained"
      color="primary"
      onClick={handleQuickSearch}
      fullWidth
      sx={{ py: 1.3, fontWeight: 'bold', letterSpacing: 0.5 }}
    >
      Search
    </Button>
  </Grid>

</Grid>




</Grid>

        </form>



        <Grid item xs={12} sx={{ mt: 4 }}>
          <Paper sx={{ width: '100%' }}>
            <TableContainer component={Paper} className={`${styles.list_tbl_container} printable-table`}>
              <Table size="small" stickyHeader className="printable-table">
                <TableHead className={styles.table_head_container}>
                  <StyledTableRow>
                    <StyledTableCell align="center">Sr No</StyledTableCell>
                    <StyledTableCell align="left">Date</StyledTableCell>
                    <StyledTableCell align="left">Cylinder No</StyledTableCell>
                    <StyledTableCell align="left">Tag No</StyledTableCell>
                    <StyledTableCell align="left">Current Location</StyledTableCell>
                    <StyledTableCell align="left">Status</StyledTableCell>
                    <StyledTableCell align="left">Note</StyledTableCell>
                    <StyledTableCell align="left">trans name</StyledTableCell>
                    <StyledTableCell align="left">trans date</StyledTableCell>

                  </StyledTableRow>
                </TableHead>
                <TableBody sx={{ backgroundColor: '#fff' }}>
                  {loading ? (
                    <StyledTableRow>
                      <StyledTableCell colSpan={9} align="center">
                        <Typography>Loading data...</Typography>
                      </StyledTableCell>
                    </StyledTableRow>
                  ) : filteredData.length === 0 ? (
                    <StyledTableRow>
                      <StyledTableCell colSpan={9} align="center">
                        <Typography>No data found</Typography>
                      </StyledTableCell>
                    </StyledTableRow>
                  ) : (
                    filteredData.map((row, index) => (
                      <StyledTableRow key={index}>
                        <StyledTableCell align="center">{row.sr_no}</StyledTableCell>
                        <StyledTableCell>{row.date}</StyledTableCell> {/* fixed key */}
                        <StyledTableCell>{row.cyl_no}</StyledTableCell> {/* fixed key */}
                        <StyledTableCell>{row.tag_no}</StyledTableCell>
                        <StyledTableCell>{row.current_location}</StyledTableCell>
                        <StyledTableCell>{row.status}</StyledTableCell>
                        <StyledTableCell>{row.remarks}</StyledTableCell>
                        <StyledTableCell>{row.trans_name}</StyledTableCell> {/* fixed key */}
                        <StyledTableCell>{row.trans_date}</StyledTableCell>
                      </StyledTableRow>

                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Snackbar */}
        <ReusableSnackbar
          open={!!successMessage}
          onClose={() => setSuccessMessage('')}
          message={successMessage}
          autoHideDuration={2000}
          vertical="top"
          horizontal="right"
          className="no-print"
        />


      </Box>
    </Box>
  );
};

export default QuickSearch;